import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Progress, Segmented, Drawer, Card, Empty, Spin, Alert, Tabs,
} from 'antd';
import { TeamOutlined, DownloadOutlined, TagOutlined, FileTextOutlined, UnorderedListOutlined, CalendarOutlined, AppstoreOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import ResizableTitle from '../components/ResizableTitle';
import { formatTS } from '../utils/time';

import { selectFilter } from '../utils/selectFilter';
import { scrollWidth } from '../utils/tableLayout';
const STATUS_LABEL = {
  assigned:          { color: 'default', text: 'Draft' },
  in_progress:       { color: 'blue',    text: 'In Progress' },
  completed:         { color: 'green',   text: 'Completed' },
  rescore_requested: { color: 'purple',  text: 'Rescore Requested' },
  canceled:          { color: 'red',     text: 'Canceled' },
};

const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

const { RangePicker } = DatePicker;

// The By Campaign / By Eval Form / By Agent toggle only drives the agent table
// and the month-over-month pivot; it means nothing on the section/question tabs.
const VIEW_MODE_TABS = new Set(['agents', 'month']);

function scoreColor(pct) {
  if (pct == null) return '#8c8c8c';
  if (pct >= 80) return '#52c41a';
  if (pct >= 60) return '#faad14';
  return '#ff4d4f';
}

const METRIC_COLS = [
  {
    title: 'Qty Evals Pending',
    dataIndex: 'PendingCount',
    key: 'PendingCount',
    width: 145,
    align: 'center',
    sorter: (a, b) => a.PendingCount - b.PendingCount,
    render: (v) => v > 0 ? <Tag color="blue">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Rescore Requested',
    dataIndex: 'RescoreCount',
    key: 'RescoreCount',
    width: 170,
    align: 'center',
    sorter: (a, b) => a.RescoreCount - b.RescoreCount,
    render: (v) => v > 0 ? <Tag color="purple">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Completed',
    dataIndex: 'CompletedCount',
    key: 'CompletedCount',
    width: 130,
    align: 'center',
    sorter: (a, b) => a.CompletedCount - b.CompletedCount,
    render: (v) => v > 0 ? <Tag color="green">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Avg Completed Score',
    dataIndex: 'AvgScore',
    key: 'AvgScore',
    width: 200,
    sorter: (a, b) => (a.AvgScore ?? -1) - (b.AvgScore ?? -1),
    render: (pct) => {
      if (pct == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
      return (
        <Typography.Text strong style={{ color: scoreColor(pct), whiteSpace: 'nowrap' }}>
          {pct}%
        </Typography.Text>
      );
    },
  },
  {
    title: 'Target (Gold)',
    dataIndex: 'GoalTargetPct',
    key: 'GoalTargetPct',
    width: 120,
    align: 'right',
    sorter: (a, b) => (a.GoalTargetPct ?? -1) - (b.GoalTargetPct ?? -1),
    render: (v) => v != null ? `${v}%` : <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  {
    title: 'Total Pts',
    dataIndex: 'TotalEarned',
    key: 'TotalEarned',
    width: 100,
    align: 'right',
    sorter: (a, b) => a.TotalEarned - b.TotalEarned,
    render: (v) => v ?? 0,
  },
  {
    title: 'Total Possible Pts',
    dataIndex: 'TotalMax',
    key: 'TotalMax',
    width: 150,
    align: 'right',
    sorter: (a, b) => a.TotalMax - b.TotalMax,
    render: (v) => v ?? 0,
  },
];

const DRILL_COLS = (navigate) => [
  {
    title: 'Status',
    dataIndex: 'Status',
    key: 'Status',
    width: 160,
    render: (s) => {
      const m = STATUS_LABEL[s] || { color: 'default', text: s };
      return <Tag color={m.color}>{m.text}</Tag>;
    },
  },
  {
    title: 'Call Date',
    dataIndex: 'CallDate',
    key: 'CallDate',
    width: 105,
    render: (v) => v ?? '—',
  },
  {
    title: 'Campaign',
    dataIndex: 'Campaign',
    key: 'Campaign',
    width: 150,
    render: (v) => v ?? '—',
  },
  {
    title: 'Eval Form',
    key: 'Form',
    width: 200,
    render: (_, r) => r.FormName ?? '—',
  },
  {
    title: 'Score',
    dataIndex: 'Score',
    key: 'Score',
    width: 170,
    render: (pct) => {
      if (pct == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress percent={pct} showInfo={false} strokeColor={scoreColor(pct)}
            size="small" style={{ flex: 1, minWidth: 60 }} />
          <Typography.Text strong style={{ color: scoreColor(pct), whiteSpace: 'nowrap' }}>{pct}%</Typography.Text>
        </div>
      );
    },
  },
  {
    title: 'Pts / Max',
    key: 'Pts',
    width: 100,
    align: 'right',
    render: (_, r) => r.TotalEarned != null ? `${r.TotalEarned} / ${r.TotalMax}` : '—',
  },
  {
    title: 'Assigned To',
    dataIndex: 'AssignedToName',
    key: 'AssignedToName',
    width: 150,
  },
  {
    title: 'Created',
    dataIndex: 'CreatedAt',
    key: 'CreatedAt',
    width: 140,
    render: (v) => formatTS(v),
  },
  {
    title: '',
    key: 'view',
    width: 60,
    render: (_, r) => (
      <Button size="small" type="link" onClick={(e) => { e.stopPropagation(); navigate(`/cases/${r.ID}`); }}>
        View
      </Button>
    ),
  },
];

// Pivot tables: the agent's name with Full Time / Part Time under it.
// Paycor status under the agent's name when it is not Active (on leave,
// termed), Paycor's run-together words spaced: LeaveWithPay -> Leave With Pay.
// Display only: the Agent Name sort is still by name.
const paycorStatus = (r) => (r.PaycorStatus ? (
  <div><Typography.Text type="warning" style={{ fontSize: 12 }}>
    {r.PaycorStatus.replace(/([a-z])([A-Z])/g, '$1 $2')}
  </Typography.Text></div>
) : null);

const agentWithFt = (name, r) => (
  <div>
    <div>{name}</div>
    {r.Employment && <div style={{ fontSize: 12, color: '#595959' }}>{r.Employment}</div>}
    {paycorStatus(r)}
  </div>
);

/**
 * `embedded` (Campaign Summary > Evaluations > Agent Stats) renders this page
 * scoped to the campaign and call-date window passed in: no heading, and no
 * Call Date, Eval Created On or Campaign filters -- Eval Form and Skill stay.
 */
export default function EvalAgentStatsPage({ embedded = false, campaignId = null, from = null, to = null, skills: groupSkills = null }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  // A failed /api/agent-stats call used to render exactly like a legitimately
  // empty result, which hid a server error on this page for weeks. Keep them
  // distinguishable.
  const [loadError, setLoadError] = useState(null);
  const [forms, setForms]         = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [skills, setSkills]       = useState([]);
  const [viewMode, setViewMode]   = useState('campaign');
  const [activeTab, setActiveTab] = useState('agents');
  const [colWidths, setColWidths] = useState({});
  const [drillOpen, setDrillOpen]     = useState(false);
  const [drillTitle, setDrillTitle]   = useState('');
  const [drillRows, setDrillRows]     = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [pivotRows, setPivotRows]       = useState([]);
  const [pivotLoading, setPivotLoading] = useState(false);
  const [secRows, setSecRows]           = useState([]);
  const [secLoading, setSecLoading]     = useState(false);
  const [secMode, setSecMode]           = useState('summary');
  const [qRows, setQRows]               = useState([]);
  const [qLoading, setQLoading]         = useState(false);
  // Missed Acknowledgement Deadline: the late evaluations, grouped by agent on the
  // tab; clicking an agent's count lists theirs in a side panel.
  const [pdaRows, setPdaRows]           = useState([]);
  const [pdaLoading, setPdaLoading]     = useState(false);
  const [pdaAgent, setPdaAgent]         = useState(null);   // { key, AgentName, unackedOnly } | null
  const [qMode, setQMode]               = useState('summary');
  const [secCaseRows, setSecCaseRows]       = useState([]);
  const [secCaseLoading, setSecCaseLoading] = useState(false);
  const [secCaseAgentId, setSecCaseAgentId] = useState(null);

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  useEffect(() => {
    api.get('/api/eval-forms/names')
      .then((r) => setForms(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
    api.get('/api/agent-stats/options')
      .then((r) => {
        setCampaigns(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setSkills(r.data.skills.map((s) => ({ label: s, value: s })));
      })
      .catch(() => {});
  }, []);

  // Reads current form values so view-mode toggles automatically refetch with active filters
  const fetchStats = useCallback(async () => {
    const values = form.getFieldsValue();
    setRows([]);
    setLoadError(null);
    setLoading(true);
    try {
      const params = { group_by: viewMode };
      if (values.callDateRange?.[0]) params.call_date_start = values.callDateRange[0].format('YYYY-MM-DD');
      if (values.callDateRange?.[1]) params.call_date_end   = values.callDateRange[1].format('YYYY-MM-DD');
      if (values.createdRange?.[0])  params.created_start   = values.createdRange[0].format('YYYY-MM-DD');
      if (values.createdRange?.[1])  params.created_end     = values.createdRange[1].format('YYYY-MM-DD');
      if (values.form_names?.length)  params.form_names      = values.form_names.join(',');
      if (values.campaigns?.length)  params.campaigns       = values.campaigns.join(',');
      if (values.skills?.length)     params.skills          = values.skills.join(',');
      if (values.excludeTermedFrom) params.exclude_termed_from = values.excludeTermedFrom.format('YYYY-MM-DD');

      const res = await api.get('/api/agent-stats', { params });
      setRows(res.data);
    } catch (err) {
      setLoadError(err?.response?.data?.error || err?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [form, viewMode]);

  const fetchPivot = useCallback(async () => {
    const values = form.getFieldsValue();
    setPivotRows([]);
    setPivotLoading(true);
    try {
      const params = { group_by: 'pivot', pivot_by: viewMode };
      if (values.callDateRange?.[0]) params.call_date_start = values.callDateRange[0].format('YYYY-MM-DD');
      if (values.callDateRange?.[1]) params.call_date_end   = values.callDateRange[1].format('YYYY-MM-DD');
      if (values.createdRange?.[0])  params.created_start   = values.createdRange[0].format('YYYY-MM-DD');
      if (values.createdRange?.[1])  params.created_end     = values.createdRange[1].format('YYYY-MM-DD');
      if (values.form_names?.length)  params.form_names      = values.form_names.join(',');
      if (values.campaigns?.length)  params.campaigns       = values.campaigns.join(',');
      if (values.skills?.length)     params.skills          = values.skills.join(',');
      if (values.excludeTermedFrom) params.exclude_termed_from = values.excludeTermedFrom.format('YYYY-MM-DD');
      const res = await api.get('/api/agent-stats', { params });
      setPivotRows(res.data);
    } catch (err) {
      setPivotRows([]);
      // Same endpoint as the table above, so one banner covers both.
      setLoadError(err?.response?.data?.error || err?.message || 'Request failed');
    } finally {
      setPivotLoading(false);
    }
  }, [form, viewMode]);

  const buildSQParams = useCallback((extra = {}) => {
    const values = form.getFieldsValue();
    const p = { ...extra };
    if (values.callDateRange?.[0]) p.call_date_start = values.callDateRange[0].format('YYYY-MM-DD');
    if (values.callDateRange?.[1]) p.call_date_end   = values.callDateRange[1].format('YYYY-MM-DD');
    if (values.createdRange?.[0])  p.created_start   = values.createdRange[0].format('YYYY-MM-DD');
    if (values.createdRange?.[1])  p.created_end     = values.createdRange[1].format('YYYY-MM-DD');
    if (values.form_names?.length)  p.form_names      = values.form_names.join(',');
    if (values.campaigns?.length)  p.campaigns       = values.campaigns.join(',');
    if (values.skills?.length)     p.skills          = values.skills.join(',');
    if (values.excludeTermedFrom) p.exclude_termed_from = values.excludeTermedFrom.format('YYYY-MM-DD');
    return p;
  }, [form]);

  const fetchSecStats = useCallback(async () => {
    setSecRows([]);
    setSecLoading(true);
    try {
      const apiMode = secMode === 'month_pivot' || secMode === 'occ_pivot' ? 'month' : secMode;
      const res = await api.get('/api/eval-analytics/agent-sections', { params: buildSQParams({ group_by: apiMode }) });
      setSecRows(res.data);
    } catch { setSecRows([]); } finally { setSecLoading(false); }
  }, [buildSQParams, secMode]);

  // One row per evaluation + section, the same feed the evaluation detail page
  // uses for its Section History list -- here across every agent the filters
  // reach rather than one.
  const fetchSecCaseHistory = useCallback(async () => {
    setSecCaseRows([]);
    // The agent filter lists names from the previous result set, so drop it
    // when those rows go away rather than leaving a selection that hides
    // everything.
    setSecCaseAgentId(null);
    setSecCaseLoading(true);
    try {
      const res = await api.get('/api/eval-analytics/agent-sections', { params: buildSQParams({ group_by: 'section_case' }) });
      setSecCaseRows(res.data || []);
    } catch { setSecCaseRows([]); } finally { setSecCaseLoading(false); }
  }, [buildSQParams]);

  const fetchQStats = useCallback(async () => {
    setQRows([]);
    setQLoading(true);
    try {
      const apiMode = qMode === 'month_pivot' ? 'month' : qMode;
      const res = await api.get('/api/eval-analytics/agent-questions', { params: buildSQParams({ group_by: apiMode }) });
      setQRows(res.data);
    } catch { setQRows([]); } finally { setQLoading(false); }
  }, [buildSQParams, qMode]);

  const fetchPastDueAcks = useCallback(async () => {
    setPdaRows([]);
    setPdaLoading(true);
    try {
      const res = await api.get('/api/agent-stats/past-due-acks', { params: buildSQParams() });
      setPdaRows(res.data || []);
    } catch { setPdaRows([]); } finally { setPdaLoading(false); }
  }, [buildSQParams]);

  const pdaByAgent = useMemo(() => {
    const m = {};
    pdaRows.forEach((r) => {
      const key = r.AgentUserID ?? `name:${r.AgentName}`;
      const a = (m[key] ||= { key, AgentName: r.AgentName, Qty: 0, Unacked: 0 });
      a.Qty += 1;
      if (!r.AcknowledgedAt) a.Unacked += 1;
    });
    return Object.values(m).sort((a, b) => b.Qty - a.Qty || (a.AgentName || '').localeCompare(b.AgentName || ''));
  }, [pdaRows]);
  const pdaAgentRows = pdaAgent
    ? pdaRows.filter((r) => (r.AgentUserID ?? `name:${r.AgentName}`) === pdaAgent.key
        && (!pdaAgent.unackedOnly || !r.AcknowledgedAt))
    : [];

  // Default call date to current month on first load; embedded, the page
  // above's campaign and window instead.
  const scopeValues = () => ({
    callDateRange: from && to ? [dayjs(from), dayjs(to)] : [dayjs().startOf('month'), dayjs()],
    campaigns: campaignId ? [campaignId] : [],
    // The Campaign Summary's Skill Group fills the Skill filter (still editable).
    ...(embedded ? { skills: groupSkills || [] } : {}),
  });
  useEffect(() => {
    if (embedded) form.setFieldsValue(scopeValues());
    else form.setFieldsValue({ callDateRange: [dayjs().startOf('month'), dayjs()] });
  }, []); // eslint-disable-line

  // Embedded: follow the page above when it changes campaign or window.
  const firstScope = useRef(true);
  useEffect(() => {
    if (!embedded) return;
    if (firstScope.current) { firstScope.current = false; return; }
    form.setFieldsValue(scopeValues());
    fetchStats(); fetchPivot(); fetchSecStats(); fetchQStats(); fetchSecCaseHistory(); fetchPastDueAcks();
  }, [embedded, campaignId, from, to, groupSkills]); // eslint-disable-line

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchPivot(); }, [fetchPivot]);
  useEffect(() => { fetchSecStats(); }, [fetchSecStats]);
  useEffect(() => { fetchQStats(); }, [fetchQStats]);
  useEffect(() => { fetchSecCaseHistory(); }, [fetchSecCaseHistory]);
  useEffect(() => { fetchPastDueAcks(); }, [fetchPastDueAcks]);

  const openDrill = useCallback(async (record) => {
    const values = form.getFieldsValue();
    const label =
      record.Campaign ? `${record.AgentName} — ${record.Campaign}` :
      record.FormName ? `${record.AgentName} — ${record.FormName}` :
      record.AgentName;
    setDrillTitle(label);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillRows([]);
    try {
      // Rows are grouped by the linked QAUsers record, so drill by ID when there
      // is one; AgentName is that user's display name and would not match the
      // stored call-log column.
      const params = record.AgentUserID
        ? { agent_user_id: record.AgentUserID }
        : { agent_name: record.AgentName };

      if (values.callDateRange?.[0]) params.start_date = values.callDateRange[0].format('YYYY-MM-DD');
      if (values.callDateRange?.[1]) params.end_date   = values.callDateRange[1].format('YYYY-MM-DD');
      if (values.createdRange?.[0])  params.created_start = values.createdRange[0].format('YYYY-MM-DD');
      if (values.createdRange?.[1])  params.created_end   = values.createdRange[1].format('YYYY-MM-DD');
      // Campaign/form modes: narrow drill to that specific campaign or form
      if (record.Campaign) params.campaigns = record.Campaign;
      else if (values.campaigns?.length) params.campaigns = values.campaigns.join(',');
      if (record.FormName) params.form_names = record.FormName;
      else if (values.form_names?.length) params.form_names = values.form_names.join(',');
      if (values.skills?.length)     params.skills        = values.skills.join(',');
      if (values.excludeTermedFrom) params.exclude_termed_from = values.excludeTermedFrom.format('YYYY-MM-DD');

      const res = await api.get('/api/agent-stats/drill', { params });
      setDrillRows(res.data);
    } catch {
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  }, [form]);

  const agentCol = {
    title: 'Agent Name',
    dataIndex: 'AgentName',
    key: 'AgentName',
    sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? ''),
    render: (v, r) => (r.PaycorStatus ? <div>{v}{paycorStatus(r)}</div> : v),
  };

  // Full Time / Part Time, from Paycor.
  const employmentCol = {
    title: 'FT / PT',
    dataIndex: 'Employment',
    key: 'Employment',
    width: 110,
    sorter: (a, b) => (a.Employment ?? '').localeCompare(b.Employment ?? ''),
    render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span>,
  };

  const campaignCol = {
    title: 'Campaign',
    dataIndex: 'Campaign',
    key: 'Campaign',
    width: 180,
    sorter: (a, b) => (a.Campaign ?? '').localeCompare(b.Campaign ?? ''),
  };

  const formCol = {
    title: 'Eval Form',
    key: 'Form',
    width: 220,
    sorter: (a, b) => (a.FormName ?? '').localeCompare(b.FormName ?? ''),
    render: (_, r) => r.FormName ?? '—',
  };

  const secondaryCol =
    viewMode === 'campaign' ? campaignCol :
    viewMode === 'form'     ? formCol : null;

  const columns = (secondaryCol
    ? [agentCol, employmentCol, secondaryCol, ...METRIC_COLS]
    : [agentCol, employmentCol, ...METRIC_COLS]
  ).map(toResizable);

  const tableRowKey =
    viewMode === 'campaign' ? (r) => `${r.AgentName}|${r.Campaign}` :
    viewMode === 'form'     ? (r) => `${r.AgentName}|${r.FormID}` :
    'AgentName';

  const paginationLabel = viewMode === 'agent' ? 'agents' : 'rows';

  const tableSummary = () => {
    const totEarned  = rows.reduce((s, r) => s + (r.TotalEarned  ?? 0), 0);
    const totMax     = rows.reduce((s, r) => s + (r.TotalMax     ?? 0), 0);
    const totPending = rows.reduce((s, r) => s + (r.PendingCount ?? 0), 0);
    const totRescore = rows.reduce((s, r) => s + (r.RescoreCount ?? 0), 0);
    const totDone    = rows.reduce((s, r) => s + (r.CompletedCount ?? 0), 0);
    const avgPct     = totMax > 0 ? Math.round((totEarned / totMax) * 1000) / 10 : null;
    const boldCell   = { fontWeight: 600 };
    const off        = (secondaryCol ? 1 : 0) + 1;  // extra columns: FT / PT, and Campaign or Eval Form
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: '#fafafa' }}>
          <Table.Summary.Cell index={0} style={boldCell}>Total</Table.Summary.Cell>
          <Table.Summary.Cell index={1} />
          {secondaryCol && <Table.Summary.Cell index={2} />}
          <Table.Summary.Cell index={1 + off} align="center" style={boldCell}>
            {totPending > 0 ? totPending : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2 + off} align="center" style={boldCell}>
            {totRescore > 0 ? totRescore : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3 + off} align="center" style={boldCell}>
            {totDone > 0 ? totDone : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4 + off} style={boldCell}>
            {avgPct != null ? (
              <Typography.Text strong style={{ color: scoreColor(avgPct), whiteSpace: 'nowrap' }}>
                {avgPct}%
              </Typography.Text>
            ) : <span style={{ color: '#bfbfbf' }}>—</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5 + off} align="right" style={boldCell}>
            <span style={{ color: '#bfbfbf' }}>—</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6 + off} align="right" style={boldCell}>{totEarned}</Table.Summary.Cell>
          <Table.Summary.Cell index={7 + off} align="right" style={boldCell}>{totMax}</Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  // Options come from the rows already loaded rather than the filter-options
  // endpoint, so the list only ever offers agents actually in the table --
  // picking one can never empty it. Keyed on AgentUserID; AgentName is the
  // QAUsers display name the API resolved and is a label only.
  const secCaseAgents = useMemo(() => {
    const seen = new Map();
    secCaseRows.forEach((r) => {
      if (r.AgentUserID != null && !seen.has(r.AgentUserID)) seen.set(r.AgentUserID, r.AgentName);
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [secCaseRows]);

  const secCaseVisible = useMemo(() => (
    secCaseAgentId == null ? secCaseRows : secCaseRows.filter((r) => r.AgentUserID === secCaseAgentId)
  ), [secCaseRows, secCaseAgentId]);

  const saveSheet = (data, sheetName, fileName) => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), sheetName);
    XLSX.writeFile(wb, fileName);
  };

  const exportToExcel = () => {
    const exportRows = rows.map((r) => {
      const base = { 'Agent Name': r.AgentName, 'FT / PT': r.Employment ?? '' };
      if (viewMode === 'campaign') base['Campaign']  = r.Campaign;
      if (viewMode === 'form')     base['Eval Form'] = r.FormName ?? '—';
      base['Qty Evals Pending']       = r.PendingCount;
      base['Qty Rescore Requested']   = r.RescoreCount;
      base['Qty Completed']           = r.CompletedCount;
      base['Avg Completed Score (%)'] = r.AvgScore ?? '';
      base['Target (Gold) (%)']       = r.GoalTargetPct ?? '';
      base['Total Pts']               = r.TotalEarned;
      base['Total Possible Pts']      = r.TotalMax;
      return base;
    });
    saveSheet(exportRows, 'Agent Stats', 'eval-agent-stats.xlsx');
  };

  // The page-level Export follows the tab on screen. One button that always
  // wrote the agent table was a trap: from any other tab it silently handed
  // back a different table's file.
  const monthLabels = (rowsIn) => {
    const labels = {};
    rowsIn.forEach((r) => { if (r.MonthKey) labels[r.MonthKey] = r.Month || r.MonthKey; });
    return labels;
  };

  const exportMonthOverMonth = () => {
    const months = [...new Set(pivotRows.map((r) => r.MonthKey))].sort();
    const labels = monthLabels(pivotRows);
    const byKey = {};
    const order = [];
    pivotRows.forEach((r) => {
      const k =
        viewMode === 'campaign' ? `${r.AgentName}||${r.Campaign}` :
        viewMode === 'form'     ? `${r.AgentName}||${r.FormName}` :
        r.AgentName;
      if (!byKey[k]) {
        const base = { 'Agent Name': r.AgentName, 'FT / PT': r.Employment ?? '' };
        if (viewMode === 'campaign') base['Campaign']  = r.Campaign;
        if (viewMode === 'form')     base['Eval Form'] = r.FormName ?? '—';
        byKey[k] = base;
        order.push(k);
      }
      const label = labels[r.MonthKey] || r.MonthKey;
      // A month is two numbers on screen; a spreadsheet can only sort or
      // average them if each gets its own column.
      byKey[k][`${label} Avg Score (%)`] = r.AvgScore ?? '';
      byKey[k][`${label} Completed`]     = r.CompletedCount ?? 0;
    });
    const out = order.map((k) => {
      const row = byKey[k];
      months.forEach((mk) => {
        const label = labels[mk] || mk;
        if (row[`${label} Avg Score (%)`] === undefined) row[`${label} Avg Score (%)`] = '';
        if (row[`${label} Completed`]     === undefined) row[`${label} Completed`]     = 0;
      });
      return row;
    });
    saveSheet(out, 'Month Over Month', 'eval-month-over-month.xlsx');
  };

  const exportSectionStats = () => {
    if (secMode === 'month_pivot' || secMode === 'occ_pivot') {
      const occ = secMode === 'occ_pivot';
      const months = [...new Set(secRows.map((r) => r.MonthKey))].sort();
      const labels = monthLabels(secRows);
      const map = {}; const order = [];
      secRows.forEach((r) => {
        const k = `${r.AgentName}||${r.SectionName}`;
        if (!map[k]) { map[k] = { 'Agent Name': r.AgentName, 'FT / PT': r.Employment ?? '', 'Section': r.SectionName }; order.push(k); }
        map[k][labels[r.MonthKey] || r.MonthKey] = occ ? (r.Occurrences ?? 0) : (r.AvgPct ?? '');
      });
      const out = order.map((k) => {
        const row = map[k];
        months.forEach((mk) => {
          const label = labels[mk] || mk;
          if (row[label] === undefined) row[label] = occ ? 0 : '';
        });
        return row;
      });
      saveSheet(out, occ ? 'Occurrences by Month' : 'Section Stats', 'eval-section-stats.xlsx');
      return;
    }
    saveSheet(secRows.map((r) => {
      const base = { 'Agent Name': r.AgentName };
      if (secMode === 'month') base['Month'] = r.Month;
      base['Section']            = r.SectionName;
      base['# Cases']            = r.CaseCount;
      base['Occurrences']        = r.Occurrences ?? 0;
      base['Avg Score (%)']      = r.AvgPct ?? '';
      base['Total Pts']          = r.TotalEarned;
      base['Total Possible Pts'] = r.TotalMax;
      return base;
    }), 'Section Stats', 'eval-section-stats.xlsx');
  };

  const exportQuestionStats = () => {
    if (qMode === 'month_pivot') {
      const months = [...new Set(qRows.map((r) => r.MonthKey))].sort();
      const labels = monthLabels(qRows);
      const map = {}; const order = [];
      qRows.forEach((r) => {
        const k = `${r.AgentName}||${r.SectionName}||${r.QuestionText}`;
        if (!map[k]) { map[k] = { 'Agent Name': r.AgentName, 'FT / PT': r.Employment ?? '', 'Section': r.SectionName, 'Question': r.QuestionText }; order.push(k); }
        map[k][labels[r.MonthKey] || r.MonthKey] = r.AvgPct ?? '';
      });
      const out = order.map((k) => {
        const row = map[k];
        months.forEach((mk) => { const label = labels[mk] || mk; if (row[label] === undefined) row[label] = ''; });
        return row;
      });
      saveSheet(out, 'Question Stats', 'eval-question-stats.xlsx');
      return;
    }
    saveSheet(qRows.map((r) => {
      const base = { 'Agent Name': r.AgentName };
      if (qMode === 'month') base['Month'] = r.Month;
      base['Section']            = r.SectionName;
      base['Question']           = r.QuestionText;
      base['# Answers']          = r.AnswerCount;
      base['Avg Score (%)']      = r.AvgPct ?? '';
      base['Total Pts']          = r.TotalEarned;
      base['Total Possible Pts'] = r.TotalMax;
      return base;
    }), 'Question Stats', 'eval-question-stats.xlsx');
  };

  // Exports what is on screen, so the agent filter narrows the file too.
  const exportSecCaseHistory = () => {
    saveSheet(secCaseVisible.map((r) => ({
      'Agent Name': r.AgentName,
      'Eval ID':    r.CaseID,
      'Section':    r.SectionName,
      'Call Date':  (r.CallDate || '').slice(0, 10),
      'Created':    r.CreatedDate,
      // Ids only: a cell holding several sessions has to stay one value, and
      // the id is what makes a session findable afterwards.
      'Coachings':  (r.Coachings || []).map((c) => `#${c.ID} (${SESSION_STATUS_LABEL[c.Status] ?? c.Status})`).join(', '),
      'Score (%)':  r.ScorePct ?? '',
    })), 'Section History', 'eval-section-history.xlsx');
  };

  // Two sheets: the per-agent counts on the tab, and every late evaluation.
  const exportPastDueAcks = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pdaByAgent.map((a) => ({
      'Agent Name': a.AgentName, 'Missed Qty': a.Qty, 'Still Unacknowledged': a.Unacked,
    }))), 'By Agent');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pdaRows.map((r) => ({
      'Agent':            r.AgentName,
      'Eval ID':          r.ID,
      'Contact ID':       r.ContactID ?? '',
      'Campaign':         r.Campaign ?? '',
      'Eval Form':        r.FormName ?? '',
      'Call Date':        r.CallDate ?? '',
      'Completed On':     formatTS(r.CompletedAt),
      'Acknowledge Deadline': r.Deadline,
      'Acknowledged On':  r.AcknowledgedAt ? formatTS(r.AcknowledgedAt) : '',
      'Auto Acknowledged': r.AutoAcknowledged ? 'Yes' : 'No',
      'Days Late':        r.DaysLate,
    }))), 'Evaluations');
    XLSX.writeFile(wb, 'eval-missed-acknowledgement-deadline.xlsx');
  };

  const TAB_EXPORTS = {
    agents:    { run: exportToExcel,         count: rows.length },
    month:     { run: exportMonthOverMonth,  count: pivotRows.length },
    sections:  { run: exportSectionStats,    count: secRows.length },
    questions: { run: exportQuestionStats,   count: qRows.length },
    history:   { run: exportSecCaseHistory,  count: secCaseVisible.length },
    pastdue:   { run: exportPastDueAcks,     count: pdaRows.length },
  };
  const activeExport = TAB_EXPORTS[activeTab] || TAB_EXPORTS.agents;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        {embedded ? <span /> : (
          <Typography.Title level={4} style={{ margin: 0 }}>
            Eval Agent Stats
          </Typography.Title>
        )}
        {VIEW_MODE_TABS.has(activeTab) && (
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { label: 'By Campaign',  value: 'campaign', icon: <TagOutlined /> },
              { label: 'By Eval Form', value: 'form',     icon: <FileTextOutlined /> },
              { label: 'By Agent',     value: 'agent',    icon: <TeamOutlined /> },
            ]}
          />
        )}
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={() => { fetchStats(); fetchPivot(); fetchSecStats(); fetchQStats(); fetchSecCaseHistory(); fetchPastDueAcks(); }}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="callDateRange" label="Call Date" hidden={embedded}>
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="createdRange" label="Eval Created On" hidden={embedded}>
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="form_names" label="Eval Form">
          <Select
            mode="multiple"
            allowClear
            placeholder="All forms"
            options={forms}
            style={{ width: 280 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="campaigns" label="Campaign" hidden={embedded}>
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All campaigns"
            options={campaigns}
            style={{ width: 260 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="skills" label="Skill">
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All skills"
            options={skills}
            style={{ width: 240 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="excludeTermedFrom" label="Exclude w/ Term Date On/After" hidden={embedded}>
          <DatePicker style={{ width: 150 }} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading || pivotLoading}>
              Filter
            </Button>
            <Button onClick={() => {
              // Embedded, the campaign and window belong to the page above: only
              // the filters shown here are cleared.
              if (embedded) form.setFieldsValue({ form_names: [], skills: [], createdRange: null });
              else form.resetFields();
              fetchStats(); fetchPivot(); fetchSecStats(); fetchQStats(); fetchSecCaseHistory(); fetchPastDueAcks();
            }}>
              Reset
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={activeExport.run}
              disabled={activeExport.count === 0}
            >
              Export
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {loadError && (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message="Could not load agent stats"
          description={loadError}
          onClose={() => setLoadError(null)}
        />
      )}

      {/* Four stacked sections made the page long enough that the lower ones
          were rarely reached. Tabs rather than removing anything: the same
          content, one at a time, following error-agent-stats. The filter
          form stays above them, since it applies to all four. */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'agents',
            label: 'Agent Stats',
            children: (
              <>
                <Table
                  rowKey={tableRowKey}
                  dataSource={rows}
                  columns={columns}
                  components={{ header: { cell: ResizableTitle } }}
                  loading={loading}
                  size="small"
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} ${paginationLabel}` }}
                  scroll={{ x: 'max-content' }}
                  summary={rows.length > 0 ? tableSummary : undefined}
                  onRow={(record) => ({
                    onClick: () => openDrill(record),
                    style: { cursor: 'pointer' },
                  })}
                />
              </>
            ),
          },
          {
            key: 'month',
            label: 'Month Over Month',
            children: (
              <>
                {(() => {
                  // Build pivot table: rows keyed by (AgentName + optional secondary), columns = sorted months
                  const pivotMonths = [...new Set(pivotRows.map((r) => r.MonthKey))].sort();
                  const pivotMonthLabels = {};
                  pivotRows.forEach((r) => { pivotMonthLabels[r.MonthKey] = r.Month; });

                  const byKey = {};
                  pivotRows.forEach((r) => {
                    const k =
                      viewMode === 'campaign' ? `${r.AgentName}||${r.Campaign}` :
                      viewMode === 'form'     ? `${r.AgentName}||${r.FormName}` :
                      r.AgentName;
                    if (!byKey[k]) byKey[k] = { _key: k, AgentName: r.AgentName, Employment: r.Employment, Campaign: r.Campaign, FormName: r.FormName };
                    byKey[k][r.MonthKey] = { count: r.CompletedCount, score: r.AvgScore };
                  });
                  const pivotTableRows = Object.values(byKey).sort((a, b) =>
                    a.AgentName.localeCompare(b.AgentName) ||
                    (a.Campaign ?? a.FormName ?? '').localeCompare(b.Campaign ?? b.FormName ?? '')
                  );

                  const fixedCols = [
                    { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', fixed: 'left', width: 180, ellipsis: true, render: agentWithFt },
                    ...(viewMode === 'campaign' ? [
                      { title: 'Campaign', dataIndex: 'Campaign', key: '__campaign', fixed: 'left', width: 160, ellipsis: true },
                    ] : viewMode === 'form' ? [
                      { title: 'Eval Form', key: '__form', fixed: 'left', width: 200, ellipsis: true,
                        render: (_, r) => r.FormName ?? '—' },
                    ] : []),
                  ];

                  const pivotCols = [
                    ...fixedCols,
                    ...pivotMonths.map((mk) => ({
                      title: pivotMonthLabels[mk] || mk,
                      key: mk,
                      width: 160,
                      align: 'center',
                      render: (_, r) => {
                        const cell = r[mk];
                        if (!cell || cell.count === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
                        return (
                          <div>
                            <div style={{ color: scoreColor(cell.score), fontWeight: 600 }}>
                              {cell.score != null ? `${cell.score}%` : '—'}
                            </div>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{cell.count} completed</div>
                          </div>
                        );
                      },
                    })),
                  ];

                  return (
                    <>
                      <Typography.Title level={5} style={{ marginBottom: 8 }}>
                        Month Over Month (Call Date) — Agent Name
                      </Typography.Title>
                      <Table
                        rowKey="_key"
                        dataSource={pivotTableRows}
                        columns={pivotCols}
                        loading={pivotLoading}
                        size="small"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        locale={{ emptyText: 'No data for current filters.' }}
                      />
                    </>
                  );
                })()}
              </>
            ),
          },
          {
            key: 'sections',
            label: 'Section Stats',
            children: (
              <>
                {/* ── Section Stats ──────────────────────────────────────────────── */}
                {(() => {
                  const SEC_MODES = [
                    { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                    { label: 'By Month',       value: 'month',       icon: <CalendarOutlined /> },
                    { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                    { label: 'Occ. by Month',  value: 'occ_pivot',   icon: <WarningOutlined /> },
                  ];
                  const renderSec = () => {
                    if (secLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>;
                    if (secRows.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data for current filters." />;
                    if (secMode === 'month_pivot') {
                      const months = [...new Set(secRows.map((r) => r.MonthKey))].sort();
                      const mLabels = {};
                      secRows.forEach((r) => { mLabels[r.MonthKey] = r.Month; });
                      const seen = []; const map = {};
                      secRows.forEach((r) => {
                        const k = `${r.AgentName}||${r.SectionName}`;
                        if (!map[k]) { map[k] = { _key: k, AgentName: r.AgentName, Employment: r.Employment, SectionName: r.SectionName }; seen.push(k); }
                        map[k][r.MonthKey] = r.AvgPct;
                      });
                      const pivotData = seen.map((k) => map[k]);
                      const cols = [
                        { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', width: 180, ellipsis: true, fixed: 'left', render: agentWithFt,
                          onCell: (r, i) => {
                            const prev = i > 0 ? pivotData[i - 1].AgentName : null;
                            if (r.AgentName === prev) return { rowSpan: 0 };
                            return { rowSpan: pivotData.filter((x) => x.AgentName === r.AgentName).length };
                          },
                        },
                        { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 160, ellipsis: true, fixed: 'left' },
                        ...months.map((mk) => ({
                          title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                          render: (_, r) => {
                            const pct = r[mk];
                            if (pct == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
                            return <span style={{ color: scoreColor(pct), fontWeight: 600 }}>{pct}%</span>;
                          },
                        })),
                      ];
                      return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small" pagination={false} scroll={{ x: scrollWidth(cols) }} bordered locale={{ emptyText: 'No data.' }} />;
                    }
                    if (secMode === 'occ_pivot') {
                      const months = [...new Set(secRows.map((r) => r.MonthKey))].sort();
                      const mLabels = {};
                      secRows.forEach((r) => { mLabels[r.MonthKey] = r.Month; });
                      const seen = []; const map = {};
                      secRows.forEach((r) => {
                        const k = `${r.AgentName}||${r.SectionName}`;
                        if (!map[k]) { map[k] = { _key: k, AgentName: r.AgentName, Employment: r.Employment, SectionName: r.SectionName }; seen.push(k); }
                        map[k][r.MonthKey] = r.Occurrences ?? 0;
                      });
                      const pivotData = seen.map((k) => map[k]);
                      const cols = [
                        { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', width: 180, ellipsis: true, fixed: 'left', render: agentWithFt,
                          onCell: (r, i) => {
                            const prev = i > 0 ? pivotData[i - 1].AgentName : null;
                            if (r.AgentName === prev) return { rowSpan: 0 };
                            return { rowSpan: pivotData.filter((x) => x.AgentName === r.AgentName).length };
                          },
                        },
                        { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 160, ellipsis: true, fixed: 'left' },
                        ...months.map((mk) => ({
                          title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                          render: (_, r) => {
                            const v = r[mk];
                            if (v == null || v === 0) return <span style={{ color: '#d9d9d9' }}>0</span>;
                            return <Tag color="orange" style={{ margin: 0 }}>{v}</Tag>;
                          },
                        })),
                      ];
                      return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small" pagination={false} scroll={{ x: scrollWidth(cols) }} bordered locale={{ emptyText: 'No data.' }} />;
                    }
                    const cols = [
                      { title: 'Agent Name', dataIndex: 'AgentName',    key: 'AgentName',    width: 180, ellipsis: true, sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? '') },
                      ...(secMode === 'month' ? [{ title: 'Month', dataIndex: 'Month', key: 'Month', width: 130, sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') }] : []),
                      { title: 'Section',    dataIndex: 'SectionName',  key: 'SectionName',  ellipsis: true, sorter: (a, b) => a.SectionName.localeCompare(b.SectionName) },
                      { title: '# Cases',   dataIndex: 'CaseCount',     key: 'CaseCount',    width: 90, align: 'right', sorter: (a, b) => a.CaseCount - b.CaseCount },
                      { title: 'Occurrences', dataIndex: 'Occurrences', key: 'Occurrences', width: 115, align: 'right', sorter: (a, b) => (a.Occurrences ?? 0) - (b.Occurrences ?? 0),
                        render: (v) => v > 0 ? <Tag color="orange" style={{ margin: 0 }}>{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
                      },
                      { title: 'Avg Score', dataIndex: 'AvgPct', key: 'AvgPct', width: 200, sorter: (a, b) => (a.AvgPct ?? -1) - (b.AvgPct ?? -1),
                        render: (p) => p == null ? <span style={{ color: '#bfbfbf' }}>—</span> : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Progress percent={p} showInfo={false} strokeColor={scoreColor(p)} size="small" style={{ flex: 1, minWidth: 80 }} />
                            <span style={{ color: scoreColor(p), fontWeight: 600, whiteSpace: 'nowrap' }}>{p}%</span>
                          </div>
                        ),
                      },
                      { title: 'Total Pts',          dataIndex: 'TotalEarned', key: 'TotalEarned', width: 100, align: 'right', sorter: (a, b) => a.TotalEarned - b.TotalEarned },
                      { title: 'Total Possible Pts', dataIndex: 'TotalMax',    key: 'TotalMax',    width: 150, align: 'right', sorter: (a, b) => a.TotalMax - b.TotalMax },
                    ];
                    const rowKey = secMode === 'month'
                      ? (r) => `${r.AgentName}|${r.MonthKey}|${r.SectionName}`
                      : (r) => `${r.AgentName}|${r.SectionName}`;
                    return <Table rowKey={rowKey} dataSource={secRows} columns={cols} size="small" pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }} scroll={{ x: scrollWidth(cols) }} />;
                  };
                  return (
                    <Card title="Section Stats" size="small" style={{ marginBottom: 16 }}
                      extra={<Segmented size="small" value={secMode} onChange={setSecMode} options={SEC_MODES} />}
                    >
                      {renderSec()}
                    </Card>
                  );
                })()}
              </>
            ),
          },
          {
            key: 'questions',
            label: 'Question Stats',
            children: (
              <>
                {/* ── Question Stats ─────────────────────────────────────────────── */}
                {(() => {
                  const Q_MODES = [
                    { label: 'Summary',        value: 'summary',     icon: <UnorderedListOutlined /> },
                    { label: 'By Month',       value: 'month',       icon: <CalendarOutlined /> },
                    { label: 'By Month Pivot', value: 'month_pivot', icon: <AppstoreOutlined /> },
                  ];
                  const renderQ = () => {
                    if (qLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spin /></div>;
                    if (qRows.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data for current filters." />;
                    if (qMode === 'month_pivot') {
                      const months = [...new Set(qRows.map((r) => r.MonthKey))].sort();
                      const mLabels = {};
                      qRows.forEach((r) => { mLabels[r.MonthKey] = r.Month; });
                      const seen = []; const map = {};
                      qRows.forEach((r) => {
                        const k = `${r.AgentName}||${r.SectionName}||${r.QuestionText}`;
                        if (!map[k]) { map[k] = { _key: k, AgentName: r.AgentName, Employment: r.Employment, SectionName: r.SectionName, QuestionText: r.QuestionText }; seen.push(k); }
                        map[k][r.MonthKey] = r.AvgPct;
                      });
                      const pivotData = seen.map((k) => map[k]);
                      const cols = [
                        { title: 'Agent Name', dataIndex: 'AgentName', key: '__agent', width: 180, ellipsis: true, fixed: 'left', render: agentWithFt,
                          onCell: (r, i) => {
                            const prev = i > 0 ? pivotData[i - 1].AgentName : null;
                            if (r.AgentName === prev) return { rowSpan: 0 };
                            return { rowSpan: pivotData.filter((x) => x.AgentName === r.AgentName).length };
                          },
                        },
                        { title: 'Section', dataIndex: 'SectionName', key: '__sec', width: 160, ellipsis: true, fixed: 'left',
                          onCell: (r, i) => {
                            const prevA = i > 0 ? pivotData[i - 1].AgentName : null;
                            const prevS = i > 0 ? pivotData[i - 1].SectionName : null;
                            if (r.AgentName === prevA && r.SectionName === prevS) return { rowSpan: 0 };
                            return { rowSpan: pivotData.filter((x) => x.AgentName === r.AgentName && x.SectionName === r.SectionName).length };
                          },
                        },
                        { title: 'Question', dataIndex: 'QuestionText', key: '__q', width: 260, ellipsis: true, fixed: 'left' },
                        ...months.map((mk) => ({
                          title: mLabels[mk] || mk, key: mk, width: 110, align: 'center',
                          render: (_, r) => {
                            const pct = r[mk];
                            if (pct == null) return <span style={{ color: '#d9d9d9' }}>—</span>;
                            return <span style={{ color: scoreColor(pct), fontWeight: 600 }}>{pct}%</span>;
                          },
                        })),
                      ];
                      return <Table rowKey="_key" dataSource={pivotData} columns={cols} size="small" pagination={false} scroll={{ x: scrollWidth(cols) }} bordered locale={{ emptyText: 'No data.' }} />;
                    }
                    const cols = [
                      { title: 'Agent Name', dataIndex: 'AgentName',    key: 'AgentName',    width: 180, ellipsis: true, sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? '') },
                      ...(qMode === 'month' ? [{ title: 'Month', dataIndex: 'Month', key: 'Month', width: 130, sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') }] : []),
                      { title: 'Section',  dataIndex: 'SectionName',  key: 'SectionName',  width: 160, ellipsis: true, sorter: (a, b) => a.SectionName.localeCompare(b.SectionName) },
                      // Fixed width and wrapped: a long question would otherwise
                      // stretch the table past the screen.
                      { title: 'Question', dataIndex: 'QuestionText', key: 'QuestionText', width: 420,
                        onCell: () => ({ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }),
                        sorter: (a, b) => a.QuestionText.localeCompare(b.QuestionText) },
                      { title: '# Answers', dataIndex: 'AnswerCount', key: 'AnswerCount', width: 95, align: 'right', sorter: (a, b) => a.AnswerCount - b.AnswerCount },
                      { title: 'Avg Score', dataIndex: 'AvgPct', key: 'AvgPct', width: 200, sorter: (a, b) => (a.AvgPct ?? -1) - (b.AvgPct ?? -1),
                        render: (p) => p == null ? <span style={{ color: '#bfbfbf' }}>—</span> : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Progress percent={p} showInfo={false} strokeColor={scoreColor(p)} size="small" style={{ flex: 1, minWidth: 80 }} />
                            <span style={{ color: scoreColor(p), fontWeight: 600, whiteSpace: 'nowrap' }}>{p}%</span>
                          </div>
                        ),
                      },
                      { title: 'Total Pts',          dataIndex: 'TotalEarned', key: 'TotalEarned', width: 100, align: 'right', sorter: (a, b) => a.TotalEarned - b.TotalEarned },
                      { title: 'Total Possible Pts', dataIndex: 'TotalMax',    key: 'TotalMax',    width: 150, align: 'right', sorter: (a, b) => a.TotalMax - b.TotalMax },
                    ];
                    const rowKey = qMode === 'month'
                      ? (r) => `${r.AgentName}|${r.MonthKey}|${r.SectionName}|${r.QuestionText}`
                      : (r) => `${r.AgentName}|${r.SectionName}|${r.QuestionText}`;
                    return <Table rowKey={rowKey} dataSource={qRows} columns={cols} size="small" pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }} scroll={{ x: scrollWidth(cols) }} />;
                  };
                  return (
                    <Card title="Question Stats" size="small"
                      extra={<Segmented size="small" value={qMode} onChange={setQMode} options={Q_MODES} />}
                    >
                      {renderQ()}
                    </Card>
                  );
                })()}
              </>
            ),
          },
          {
            key: 'history',
            label: 'Occurrence Section History',
            children: (
              <>
                <Card
                  title="Section History by Evaluation"
                  size="small"
                  extra={
                    <Space>
                      <Select
                        allowClear
                        showSearch
                        placeholder="All agents"
                        style={{ width: 220 }}
                        value={secCaseAgentId}
                        onChange={setSecCaseAgentId}
                        options={secCaseAgents}
                        optionFilterProp="label"
                      />
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={exportSecCaseHistory}
                        disabled={secCaseVisible.length === 0}
                      >
                        Export
                      </Button>
                    </Space>
                  }
                >
                  <Table
                    rowKey={(r) => `${r.CaseID}||${r.SectionName}`}
                    dataSource={secCaseVisible}
                    loading={secCaseLoading}
                    size="small"
                    pagination={{ defaultPageSize: 100, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                    scroll={{ x: 'max-content' }}
                    bordered
                    locale={{ emptyText: 'No completed evaluations in this date range.' }}
                    columns={[
                      { title: 'Agent Name', dataIndex: 'AgentName', key: 'AgentName', width: 180, ellipsis: true, fixed: 'left',
                        sorter: (a, b) => (a.AgentName ?? '').localeCompare(b.AgentName ?? '') },
                      { title: 'Eval ID', dataIndex: 'CaseID', key: 'CaseID', width: 90, align: 'right',
                        sorter: (a, b) => a.CaseID - b.CaseID,
                        render: (v) => (
                          <Button type="link" size="small" style={{ padding: 0 }}
                            onClick={() => window.open(`/cases/${v}`, '_blank', 'noopener')}>{v}</Button>
                        ),
                      },
                      { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', width: 240, ellipsis: true,
                        sorter: (a, b) => (a.SectionName ?? '').localeCompare(b.SectionName ?? '') },
                      { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 110,
                        sorter: (a, b) => (a.CallDate ?? '').localeCompare(b.CallDate ?? ''),
                        render: (v) => (v || '').slice(0, 10) || <Typography.Text type="secondary">—</Typography.Text> },
                      { title: 'Created', dataIndex: 'CreatedDate', key: 'CreatedDate', width: 110,
                        render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                      {
                        // An evaluation can raise more than one session, so this
                        // is a list rather than a single value.
                        title: 'Coachings', key: 'Coachings', width: 260,
                        render: (_, r) => (r.Coachings || []).length === 0
                          ? <Typography.Text type="secondary">—</Typography.Text>
                          : (r.Coachings || []).map((c) => (
                            <Tag key={c.ID} color={SESSION_STATUS_COLOR[c.Status] ?? 'blue'}
                              style={{ cursor: 'pointer', marginBottom: 2 }}
                              onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank', 'noopener')}>
                              #{c.ID}{c.RequestType ? ` · ${c.RequestType}` : ''} · {c.CreatedOn} · {SESSION_STATUS_LABEL[c.Status] ?? c.Status}{c.Linked != null ? (c.Linked ? ' · Linked Sections' : ' · All Sections') : ''}
                            </Tag>
                          )),
                      },
                      { title: 'Score', key: 'ScorePct', width: 90, align: 'right',
                        sorter: (a, b) => (a.ScorePct ?? -1) - (b.ScorePct ?? -1),
                        render: (_, r) => r.ScorePct == null
                          ? <Typography.Text type="secondary">—</Typography.Text>
                          : `${r.ScorePct}%` },
                    ]}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'pastdue',
            label: 'Missed Acknowledgement Deadline',
            children: (
              <Card title="Missed Acknowledgement Deadline" size="small"
                extra={(
                  <Button size="small" icon={<DownloadOutlined />} onClick={exportPastDueAcks} disabled={!pdaRows.length}>
                    Export to Excel
                  </Button>
                )}>
                <Table
                  rowKey="key"
                  dataSource={pdaByAgent}
                  loading={pdaLoading}
                  size="small"
                  bordered
                  pagination={false}
                  style={{ maxWidth: 690 }}
                  locale={{ emptyText: 'No missed acknowledgement deadlines for these filters.' }}
                  columns={[
                    { title: 'Agent Name', dataIndex: 'AgentName', key: 'AgentName',
                      sorter: (a, b) => (a.AgentName || '').localeCompare(b.AgentName || '') },
                    { title: 'Missed Qty', dataIndex: 'Qty', key: 'Qty', width: 130, align: 'right',
                      defaultSortOrder: 'descend', sorter: (a, b) => a.Qty - b.Qty,
                      render: (v, r) => <Button type="link" style={{ padding: 0 }} onClick={() => setPdaAgent({ ...r, unackedOnly: false })}>{v}</Button> },
                    // Of those, the ones still not acknowledged at all.
                    { title: 'Still Unacknowledged', dataIndex: 'Unacked', key: 'Unacked', width: 170, align: 'right',
                      sorter: (a, b) => a.Unacked - b.Unacked,
                      render: (v, r) => (v
                        ? <Button type="link" danger style={{ padding: 0 }} onClick={() => setPdaAgent({ ...r, unackedOnly: true })}><strong>{v}</strong></Button>
                        : 0) },
                  ]}
                  summary={() => (pdaByAgent.length ? (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right"><strong>{pdaRows.length}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right"><strong>{pdaRows.filter((r) => !r.AcknowledgedAt).length}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                  ) : null)}
                />
              </Card>
            ),
          },
        ]}
      />

      <Drawer
        title={pdaAgent ? `${pdaAgent.unackedOnly ? 'Still Unacknowledged' : 'Missed Acknowledgement Deadline'}: ${pdaAgent.AgentName}` : ''}
        open={!!pdaAgent}
        onClose={() => setPdaAgent(null)}
        width={1000}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          dataSource={pdaAgentRows}
          size="small"
          pagination={{ defaultPageSize: 25, showSizeChanger: true, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({ onClick: () => navigate(`/cases/${record.ID}`), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'Eval ID', dataIndex: 'ID', key: 'ID', width: 80 },
            { title: 'Contact ID', dataIndex: 'ContactID', key: 'ContactID', width: 140, render: (v) => v || '—' },
            { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 130, render: (v) => v || '—' },
            { title: 'Eval Form', dataIndex: 'FormName', key: 'FormName', width: 180, ellipsis: true, render: (v) => v || '—' },
            { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 100 },
            { title: 'Completed On', dataIndex: 'CompletedAt', key: 'CompletedAt', width: 160, render: (v) => formatTS(v) },
            { title: 'Ack. Deadline', dataIndex: 'Deadline', key: 'Deadline', width: 110,
              sorter: (a, b) => a.Deadline.localeCompare(b.Deadline), defaultSortOrder: 'ascend' },
            { title: 'Acknowledged On', dataIndex: 'AcknowledgedAt', key: 'AcknowledgedAt', width: 190,
              render: (v, r) => (v
                ? <span>{formatTS(v)}{r.AutoAcknowledged && <Tag style={{ marginLeft: 6 }}>Auto</Tag>}</span>
                : <Tag color="red">Not acknowledged</Tag>) },
            { title: 'Days Late', dataIndex: 'DaysLate', key: 'DaysLate', width: 90, align: 'right',
              sorter: (a, b) => a.DaysLate - b.DaysLate },
          ]}
        />
      </Drawer>

      <Drawer
        title={drillTitle}
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
        width={1000}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          dataSource={drillRows}
          columns={DRILL_COLS(navigate)}
          loading={drillLoading}
          size="small"
          pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} evaluations` }}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => navigate(`/cases/${record.ID}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Drawer>
    </>
  );
}
